
import {
  EventMenu,
  EventModel,
  GridColumnConfig,
  Model,
  SchedulerPro,
  SchedulerResourceModel,
  ViewPreset,
  ViewPresetConfig,
} from "@bryntum/schedulerpro";
import { BryntumSchedulerPro } from "@bryntum/schedulerpro-vue-3";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faFileExcel, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EllipsisVerticalIcon,
  MinusIcon,
  PlusIcon,
} from "@heroicons/vue/24/outline";
import {
  editPlannedEvent,
  EventToEdit,
  PlanConfig,
  PlanMixins,
  PlannerComment,
  PlannerMode,
  resetLag,
  SchedulerProBaseEvent,
  SchedulerProMixins,
  SchedulerProResourceItem,
  showInfoMenuItem,
  SimplifiedPlannerProcess,
  updateParentEventsForDependencies,
  updateParentEventsForResourceAndItsParents,
  useOutages,
  useHolidaysInScheduler,
  useNonWorkingDaysInScheduler,
  useProjectDurationSettings,
} from "@oculai/planner";
import { durationService } from "@oculai/planner";
import { AxiosResponse } from "axios";
import { addDays, set } from "date-fns";
import { defineComponent } from "vue";
import { NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import * as XLSX from "xlsx";
import { segmentAnalytics, segmentExportAnalytics } from "@/assets/scripts";
import NavigationComponent from "@/components/layout/NavigationComponent.vue";
import NoDataYet from "@/components/layout/NoDataYet.vue";
import PageHeader from "@/components/layout/PageHeader.vue";
import LoadingSection from "@/components/loading_state/LoadingSection.vue";
import ModalTW from "@/components/modals/ModalTW.vue";
import NotificationsTW from "@/components/other/NotificationsTW.vue";
import { useCurrentProject } from "@/composables/project";
import { useStreams } from "@/composables/stream";
import { useConfirmationModal, useSaveBeforeLeaveConfirmationModal } from "@/composables/toast";
import i18n from "@/i18n";
import PlannerRepository from "@/repositories/PlannerRepository";
import { apiClient } from "@/repositories/clients";
import { getConfig } from "@/services/config";
import logger from "@/services/logger";
import textService from "@/services/textService";
import { HierarchyTagStore } from "@/types/HierarchyTag";
import { NonWorkingDay } from "@/types/ProjectDurationSettings";
import { SchedulerProPdfExportConfig } from "@/types/SchedulerPro";
import ActualSideBar from "@/views/planner/components/ActualSideBar.vue";
import EventEditor from "@/views/planner/components/EventEditor.vue";
import FileInputPlanner from "@/views/planner/components/FileInputPlanner.vue";
import NonWorkingDaysModal from "@/views/planner/components/NonWorkingDaysModal.vue";
import PdfExportModal from "@/views/planner/components/PdfExportModal.vue";
import ProcessSideBar from "@/views/planner/components/ProcessSideBar.vue";
import { schedulerProConfigMode } from "./SchedulerProConfigMode";

library.add(faFilePdf, faFileExcel);

const now = set(new Date(), { seconds: 0, milliseconds: 0 });

type ReducedPlannerMode = Exclude<PlannerMode, "tracker" | "hierarchy" | "process">;

const { t } = i18n.global;

export default defineComponent({
  name: "Planner",
  components: {
    PageHeader,
    FileInputPlanner,
    ModalTW,
    NoDataYet,
    ActualSideBar,
    NavigationComponent,
    ChevronDownIcon,
    ArrowUturnLeftIcon,
    ArrowUturnRightIcon,
    ArrowDownTrayIcon,
    EventEditor,
    BryntumSchedulerPro,
    Menu,
    MenuItem,
    MenuItems,
    MenuButton,
    NotificationsTW,
    LoadingSection,
    PdfExportModal,
    FontAwesomeIcon,
    PlusIcon,
    MinusIcon,
    EllipsisVerticalIcon,
    ChevronRightIcon,
    ProcessSideBar,
    ArrowUpTrayIcon,
    NonWorkingDaysModal,
  },
  mixins: [SchedulerProMixins, PlanMixins],
  data() {
    return {
      planConfig: null as PlanConfig | null,
      initialPlanConfig: null as PlanConfig | null,
      processes: null as SimplifiedPlannerProcess[] | null,
      eventEditorMode: "hide" as "hide" | "edit",
      eventRecord: {} as EventToEdit,
      alert: {
        type: "",
        title: "",
        text: "",
      } as Record<string, string>,
      openAlert: false as boolean,
      loading: false as boolean,
      hasChanges: false as boolean,
      isDependencyFeatureEnabled: false as boolean,
      isPdfExportModalOpen: false as boolean,
      presetIndex: 0 as number,
      isProcessSideBarOpen: false as boolean,
      processSideBarProcesses: [] as SimplifiedPlannerProcess[],
      processSideBarResourceFullName: "" as string,
      isActualSideBarOpen: false as boolean,
      actualSideBarResourceFullName: "" as string,
      sourceCamerasList: [] as string[],
      eventsOfResource: [] as EventModel[],
      eventDbClicked: {} as EventModel,
      clickTimeoutId: "" as unknown as ReturnType<typeof setTimeout>,
      eventDblClickSourceId: "",
      openImportModal: false as boolean,
      canImport: false as boolean,
      isFullScheduleShown: false,
      nonWorkingDaysModalOpen: false,
    };
  },
  computed: {
    noData() {
      return !this.planConfig;
    },
    EventMenu() {
      return EventMenu;
    },
    eventNow() {
      return now;
    },
    modes() {
      return ["visitor", "revision"].filter((mode) => mode) as ReducedPlannerMode[];
    },
    configs() {
      const { customer_name, site_id } = this.$route.params as Record<string, string>;

      return {
        visitor: schedulerProConfigMode("visitor", customer_name, site_id, now, this.streams),
        revision: schedulerProConfigMode("revision", customer_name, site_id, now, this.streams),
      };
    },
    schedulerProConfig() {
      return this.configs[this.currentMode];
    },
    schedulerProKey() {
      return `${this.currentMode}:${this.planConfig?.plan._id}`;
    },
    durationSettings() {
      return this.projectDurationSettings
        ? durationService.calculateSettings(
            this.projectDurationSettings.working_hours,
            this.projectDurationSettings.non_working_days,
          )
        : null;
    },
    durationSettingsWithoutDisturbances() {
      return this.projectDurationSettings
        ? durationService.calculateSettings(
            this.projectDurationSettings.working_hours,
            this.projectDurationSettings.non_working_days.filter(
              (nonWorkingDay) => nonWorkingDay.type !== "disturbance",
            ),
          )
        : null;
    },
    initialSchedulerProData() {
      if (!this.planConfig) {
        return {
          resources: null,
          events: null,
          dependencies: null,
        };
      }

      const isHolidayFn = this.createIsHolidayFn();
      const durationLabelFn = this.createEventDurationFn();
      const schedulerProData = this.createSchedulerProData(this.planConfig, now);
      return {
        ...schedulerProData,
        resources: this.mapIsHolidayFn(schedulerProData.resources, isHolidayFn),
        events: schedulerProData.events.map((event) => ({
          ...event,
          durationLabelFn,
        })),
      };
    },
    currentMode(): ReducedPlannerMode {
      return (
        this.modes.find((mode) => mode === (this.$route.query.mode as ReducedPlannerMode)) ||
        "visitor"
      );
    },
  },
  watch: {
    currentMode(value) {
      if (this.planConfig) {
        this.planConfig = {
          ...this.createPlanConfigFromSchedulerData(this.getSchedulerPro()),
          plan: this.planConfig.plan,
        } as PlanConfig;
      }
      this.$nextTick(() => {
        this.updateCriticalPathVisibility();
        this.isFullScheduleShown = value !== "visitor";
      });
    },
    planConfig(newValue: PlanConfig | null, oldValue: PlanConfig | null) {
      const scrollState = this.getSchedulerPro()?.storeScroll();

      const isProjectCompleted = this.currentProjectV2?.status === "completed";

      setTimeout(async () => {
        if (!this.getSchedulerPro()) {
          // component probably unmounted
          return;
        }
        this.setProjectStartEndTimeFlag(this.getSchedulerPro(), isProjectCompleted, t);
        this.refreshHolidaysInScheduler(this.getSchedulerPro());
        this.refreshNonWorkingDaysInScheduler(this.getSchedulerPro());

        if (oldValue && scrollState) {
          this.restoreView(this.getSchedulerPro(), this.presetIndex, scrollState);
        } else {
          this.resetView(this.getSchedulerPro(), this.schedulerProConfig.viewPreset as string);
          this.updatePresetIndex();
        }
      }, 100);
    },
    isDependencyFeatureEnabled() {
      this.getSchedulerPro().features.dependencies.disabled = !this.isDependencyFeatureEnabled;
    },
    isFullScheduleShown() {
      this.toggleNonTrackedVisibility();
    },
    projectDurationSettings() {
      if (this.getSchedulerPro()) {
        this.refreshHolidaysInScheduler(this.getSchedulerPro());
        this.refreshNonWorkingDaysInScheduler(this.getSchedulerPro());
      }
    },
  },
  mounted() {
    segmentAnalytics("Planner Visit");
    this.loading = true;
    Promise.all([this.loadPlanConfig(), this.loadProcesses()]).finally(() => {
      setTimeout(() => {
        if (!this.getSchedulerPro()) {
          // component probably unmounted
          return;
        }
        this.updatePresetIndex();
        this.updateCriticalPathVisibility();
        this.toggleNonTrackedVisibility();
        this.scrollToActiveActual(this.getSchedulerPro());
      }, 300);

      this.loading = false;
    });
    this.setPlannerImportStatus();
  },
  beforeRouteLeave(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ) {
    if (!this.hasChanges) {
      next();
    } else {
      this.showSaveBeforeLeaveConfirmationModal().then((confirmed) => {
        if (confirmed) {
          next();
        }
      });
    }
  },
  methods: {
    mapIsHolidayFn<T extends { children: T[] }>(
      resources: T[],
      isHolidayFn: (date: Date) => NonWorkingDay | undefined,
    ): T[] {
      return resources.map((resource) => ({
        ...resource,
        isHolidayFn,
        children: this.mapIsHolidayFn(resource.children, isHolidayFn),
      }));
    },
    createIsHolidayFn() {
      const getProjectDurationSettings = () => this.projectDurationSettings;
      return (date: Date) => {
        const projectDurationSettings = getProjectDurationSettings();
        if (!projectDurationSettings) {
          return undefined;
        }
        return projectDurationSettings.non_working_days.find((non_working_day) => {
          const start = non_working_day.start_date;
          const end = addDays(non_working_day.end_date, 1);
          return date >= start && date < end;
        });
      };
    },
    createEventDurationFn() {
      const getDurationSettings = (options?: { excludeDisturbances?: boolean }) =>
        options?.excludeDisturbances
          ? this.durationSettingsWithoutDisturbances
          : this.durationSettings;
      return (event: EventModel, options?: { excludeDisturbances?: boolean }) => {
        const durationSettings = getDurationSettings(options);
        if (!durationSettings) {
          return null;
        }
        const workingDays = durationService.calculateDuration(
          durationSettings,
          event.startDate as Date,
          event.endDate as Date,
        ).workingDays;
        const formattedWorkingDays = workingDays.toLocaleString(this.$i18n.locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
        });
        return `${formattedWorkingDays} ${this.$t("analytics.planner.working_days_label", {
          count: workingDays,
        })}`;
      };
    },
    changeMode(mode: PlannerMode) {
      if (this.currentMode === mode) {
        return;
      }
      if (this.hasChanges) {
        this.showConfirmationModal({
          header: this.$t("err.save_before_leave_title"),
          message: this.$t("err.save_before_mode_switch"),
          confirmAction: this.$t("buttons.leave_page"),
          cancelAction: this.$t("buttons.stay_page"),
        }).then((confirmed) => {
          if (confirmed) {
            this.resetHasChanges();
            this.$router.replace({ query: { mode } });
          }
        });
      } else {
        this.$router.replace({ query: { mode } });
      }
    },
    getSchedulerPro(): SchedulerPro {
      const schedulerProRef = this.$refs.schedulerProRef as {
        instance: { value: SchedulerPro };
      };

      return schedulerProRef?.instance?.value;
    },
    loadPlanConfig() {
      const { customer_name, site_id } = this.$route.params;
      return PlannerRepository.loadPlanConfig(customer_name as string, site_id as string)
        .then((planConfig) => {
          this.planConfig = planConfig;
          this.initialPlanConfig = planConfig;
          this.resetHasChanges();
        })
        .catch((error) => {
          if (error?.response?.status !== 404) {
            logger.error(error);
            alert(this.$t("analytics.planner.unable_to_load_plan_error_message"));
          }
        });
    },
    loadProcesses() {
      const { customer_name, site_id } = this.$route.params;
      return PlannerRepository.loadProcesses(customer_name as string, site_id as string)
        .then((processes) => {
          this.processes = processes;
        })
        .catch((error) => {
          logger.error(error);
        });
    },
    createNewRevision() {
      const { customer_name, site_id } = this.$route.params;
      if (!this.planConfig) {
        return;
      }
      this.loading = true;
      const planConfig = {
        plan: this.planConfig.plan,
        ...this.createPlanConfigFromSchedulerData(this.getSchedulerPro()),
        planner_comments: undefined,
      };

      PlannerRepository.createNewRevision(
        customer_name as string,
        site_id as string,
        planConfig,
        [],
      )
        .then((newPlanConfig) => {
          this.planConfig = newPlanConfig;
          this.initialPlanConfig = newPlanConfig;
          this.getSchedulerPro().resourceStore.commit();
          this.getSchedulerPro().eventStore.commit();
          this.getSchedulerPro().dependencyStore.commit();
          this.resetUndoRedo(this.getSchedulerPro());
          this.resetHasChanges();
        })
        .catch((error) => {
          logger.error(error);
          this.loading = false;
          this.openAlert = true;
          this.alert = {
            type: "error",
            title: "error",
            text: this.$t("analytics.planner.create_revision_error_message"),
          };
          setTimeout(() => {
            this.openAlert = false;
            this.alert = {};
          }, 3000);
        })
        .finally(() => {
          this.loading = false;
          segmentAnalytics("planner-create-new-revision");
        });
    },
    revertChangesWithConfirmation() {
      this.showConfirmationModal({
        header: this.$t("err.save_before_leave_title"),
        message: this.$t("err.save_before_mode_switch"),
        confirmAction: this.$t("buttons.revert_changes"),
        cancelAction: this.$t("buttons.stay_page"),
      }).then((confirmed) => {
        if (confirmed) {
          this.revertChanges();
        }
      });
    },
    noChangeAlert(message: string) {
      this.openAlert = true;
      this.alert = {
        type: "info",
        title: message,
        text: "",
      };
      setTimeout(() => {
        this.openAlert = false;
        this.alert = {};
      }, 3000);
    },
    undoChange() {
      if (this.getSchedulerPro().project.stm.canUndo) {
        this.getSchedulerPro().project.stm.undo();
      } else {
        this.noChangeAlert(this.$t("analytics.planner.nothing_to_undo_message"));
      }
    },
    redoChange() {
      if (this.getSchedulerPro().project.stm.canRedo) {
        this.getSchedulerPro().project.stm.redo();
      } else {
        this.noChangeAlert(this.$t("analytics.planner.nothing_to_redo_message"));
      }
    },
    handleRangeChange(event: { target: HTMLInputElement }) {
      const newPresetsValue = parseInt(event.target.value, 10);
      if (newPresetsValue > this.presetIndex) {
        // Slider moved to the right
        this.zoomIn();
      } else if (newPresetsValue < this.presetIndex) {
        // Slider moved to the left
        this.zoomOut();
      }
      this.updatePresetIndex();
    },
    zoomIn() {
      this.getSchedulerPro().zoomIn();
      this.updatePresetIndex();
    },
    zoomOut() {
      // for some reason it's possible to zoom out also when on preset 0
      // the conditions is there to fix that
      if (this.presetIndex > 0) {
        this.getSchedulerPro().zoomOut();
        this.updatePresetIndex();
      }
    },
    beforeEventAddHandler(event: SchedulerProBaseEvent) {
      editPlannedEvent(event.source, event.eventRecord.resourceId as string);
      return false;
    },
    beforeEventEditHandler(event: SchedulerProBaseEvent) {
      if (
        this.currentMode === "visitor" &&
        (event.eventRecord.getData("type") === "actual" ||
          event.eventRecord.getData("type")?.startsWith("process_"))
      ) {
        return false;
      }

      if (this.currentMode === "visitor") {
        this.handleActualSidebarOpen(event);
      } else {
        const resourceOfEvent = event.source.project.resourceStore.getById(
          event.eventRecord.getData("resourceId"),
        );
        this.eventRecord = {
          _id: event.eventRecord.id,
          resourceId: event.eventRecord.getData("resourceId"),
          type: event.eventRecord.getData("type"),
          active: event.eventRecord.getData("active"),
          isCreating: event.eventRecord.isCreating,
          startDate: event.eventRecord.startDate,
          endDate: event.eventRecord.endDate,
          name: event.eventRecord.resource.name,
          sourceId: resourceOfEvent.getData("sourceId"),
        } as EventToEdit;
        this.eventEditorMode = "edit";
      }
      return false;
    },
    async saveEvent(startDate: Date, endDate: Date) {
      resetLag(this.getSchedulerPro(), this.eventRecord._id);
      const eventToUpdate = this.getSchedulerPro().project.eventStore.getById(
        this.eventRecord._id,
      ) as EventModel;
      if (eventToUpdate.getData("startDate").getTime() !== startDate.getTime()) {
        await eventToUpdate.setStartDate(startDate, false);
      }
      eventToUpdate.setEndDate(endDate);
      const resource = this.getSchedulerPro().resourceStore.getById(this.eventRecord.resourceId);
      this.eventEditorMode = "hide";
      await updateParentEventsForResourceAndItsParents(this.getSchedulerPro(), resource.parent);
      await updateParentEventsForDependencies(this.getSchedulerPro(), resource);
    },
    cancelEdit() {
      this.eventEditorMode = "hide";
    },
    getResourceStoreChanges() {
      const changes = this.getSchedulerPro()?.resourceStore.changes;
      if (!changes) {
        return null;
      }
      const filteredChanges = {
        ...changes,
        modified: changes.modified.filter(
          (change) =>
            // change.modifications has the modified fields and also the id field in it, thus check for length 2
            !(Object.keys(change.modifications).length === 2 && change.isFieldModified("comments")),
        ),
      };
      if (
        filteredChanges.added.length === 0 &&
        filteredChanges.modified.length === 0 &&
        filteredChanges.removed.length === 0
      ) {
        return null;
      }

      return filteredChanges;
    },
    updateHasChanges() {
      this.hasChanges =
        this.currentMode !== "visitor" &&
        (!!this.getResourceStoreChanges() ||
          !!this.getEventStoreChangesWithoutProcesses(this.getSchedulerPro()) ||
          !!this.getSchedulerPro()?.dependencyStore.changes);
    },
    resetHasChanges() {
      this.hasChanges = false;
    },
    revertChanges() {
      this.planConfig = structuredClone(this.initialPlanConfig);
      this.resetHasChanges();
    },
    exportPdf(config: SchedulerProPdfExportConfig) {
      const appConfig = getConfig();
      this.isPdfExportModalOpen = false;
      this.loading = true;
      setTimeout(async () => {
        const scrollState = this.getSchedulerPro().storeScroll();
        try {
          if (config.scheduleRange === "completeview") {
            await this.resetView(
              this.getSchedulerPro(),
              this.schedulerProConfig.viewPreset as string,
            );
          }
          const { customer_name, site_id } = this.$route.params;
          this.getSchedulerPro().features.pdfExport.setConfig({
            scheduleRange: config.scheduleRange,
            rowsRange: config.rowsRange,
            exportServer: `${appConfig.API_BASE}planner-v2/plans/${customer_name}/${site_id}/export-pdf`,
            fetchOptions: {
              headers: {
                "Content-Type": "application/json",
                Authorization: apiClient.defaults.headers.common["Authorization"],
              },
            },
          });
          const { response } = (await this.getSchedulerPro().features.pdfExport.export({
            columns: (this.getSchedulerPro().columns as GridColumnConfig[]).map(
              (column) => column.id as string,
            ),
          })) as { response: { status: number } };
          if (response.status !== 200) {
            throw new Error("Unsuccessful response code");
          }
        } catch (error) {
          logger.error("Unable to generate PDF", error as Error);
          alert(this.$t("analytics.planner.unable_to_generate_pdf_error_message"));
        } finally {
          this.loading = false;
          this.restoreView(this.getSchedulerPro(), this.presetIndex, scrollState);
          segmentExportAnalytics("Planner export PDF", config.scheduleRange, config.rowsRange);
        }
      }, 100);
    },
    toggleCriticalPathVisibility() {
      this.isDependencyFeatureEnabled = !this.isDependencyFeatureEnabled;
      this.getSchedulerPro().features.dependencies.disabled = !this.isDependencyFeatureEnabled;
    },

    updateCriticalPathVisibility() {
      this.isDependencyFeatureEnabled = !this.getSchedulerPro().features.dependencies.disabled;
    },

    toggleNonTrackedVisibility() {
      if (this.isFullScheduleShown) {
        this.getSchedulerPro().resourceStore.clearFilters();
      } else {
        this.getSchedulerPro().resourceStore.filter((resource: SchedulerResourceModel) =>
          resource.getData("trackingEnabled"),
        );
      }
    },
    handleCommentAdded(comment: PlannerComment) {
      const resource = this.getSchedulerPro().resourceStore.allRecords.find(
        (resourceRecord) => resourceRecord.getData("sourceId") === comment.source_id,
      );
      if (resource) {
        resource.set("comments", [
          ...(resource.getData("comments") || []),
          { id: comment._id, source_id: comment.source_id },
        ]);
        segmentAnalytics("planner-comment-added");
      }
    },
    handleCommentDeleted(comment: PlannerComment) {
      const resource = this.getSchedulerPro().resourceStore.allRecords.find(
        (resourceRecord) => resourceRecord.getData("sourceId") === comment.source_id,
      );
      if (resource) {
        resource.set(
          "comments",
          resource.getData("comments").filter(({ _id }: PlannerComment) => _id !== comment._id),
        );
      }
    },
    handleCellDblClick(event: SchedulerProResourceItem) {
      if (this.currentMode === "visitor") {
        showInfoMenuItem(event.source, event.record.id as string);
      } else {
        editPlannedEvent(event.source, event.record.id as string);
      }
    },
    handleCellClick(event: SchedulerProResourceItem & { target: HTMLElement }) {
      if (event.target.classList.contains("plannerItemWithComments")) {
        editPlannedEvent(event.source, event.record.id as string);
      }
    },
    handleNameEdited({ eventRecord, name }: { eventRecord: EventToEdit; name: string }) {
      this.eventRecord.name = name;
      const resource = this.getSchedulerPro().resourceStore.getById(eventRecord.resourceId);
      if (!resource) {
        throw new Error("Missing resource for name editing");
      }
      resource.set("name", name);
    },
    exportXlsx() {
      this.loading = true;
      const { customer_name, site_id } = this.$route.params;
      PlannerRepository.loadPlanExportData(customer_name as string, site_id as string)
        .then((data) => {
          const workbook = XLSX.utils.book_new();
          const worksheetPlannerExport = XLSX.utils.json_to_sheet(data);
          XLSX.utils.book_append_sheet(workbook, worksheetPlannerExport, "planner data");
          XLSX.writeFile(
            workbook,
            `oculai_${textService.toNormalizedFileName(
              this.$t("analytics.planner.export_name"),
            )}.xlsx`,
          );
        })
        .catch((error) => {
          if (error?.response?.status !== 404) {
            logger.error(error);
            alert(this.$t("analytics.planner.excel_export_error_message"));
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    updatePresetIndex() {
      const viewPresetName = (this.getSchedulerPro().viewPreset as ViewPreset).id as string;
      this.presetIndex = [...(this.getSchedulerPro().presets as ViewPresetConfig[])].findIndex(
        (preset) => preset.id === viewPresetName,
      );
    },
    handleWheel(e: WheelEvent) {
      if (e.ctrlKey) {
        setTimeout(() => {
          this.updatePresetIndex();
        }, 100);
      }
    },
    openResource(resource: SchedulerResourceModel, expanded: boolean) {
      this.getSchedulerPro().toggleCollapse(resource, expanded);
      for (const child of resource.children as SchedulerResourceModel[]) {
        if (
          !child.isExpanded(this.getSchedulerPro().resourceStore) &&
          child.getData("children").length
        ) {
          if (child.getData("children").length) this.openResource(child, expanded);
        }
      }
    },
    showProcessesForEachChild(resource: SchedulerResourceModel, show: boolean) {
      if (this.processes) {
        if (
          (resource.children as Model[]).length === 0 ||
          (resource.children as Model[]).every((child) => !child.getData("trackingEnabled"))
        ) {
          this.toggleProcessEventsForResource(
            this.getSchedulerPro(),
            resource,
            this.processes,
            show,
          );
        }

        for (const child of resource.children as SchedulerResourceModel[]) {
          this.showProcessesForEachChild(child, show);
        }
      }
    },
    handleEventClick(e: SchedulerProBaseEvent) {
      clearTimeout(this.clickTimeoutId);
      this.clickTimeoutId = setTimeout(() => {
        if (e.eventRecord.resource.getData("children").length) {
          this.openResource(
            e.eventRecord.resource,
            e.eventRecord.resource.isExpanded(e.eventRecord.resourceStore),
          );
        }
        if (
          this.currentMode === "visitor" &&
          this.processes &&
          e.eventRecord.getData("type") === "actual" &&
          ((e.eventRecord.resource.children as Model[]).length === 0 ||
            (e.eventRecord.resource.children as Model[]).every(
              (child) => !child.getData("trackingEnabled"),
            ))
        ) {
          const result = this.toggleProcessEventsForResource(
            this.getSchedulerPro(),
            e.eventRecord.resource,
            this.processes,
          );
          if (result === "added") {
            segmentAnalytics("planner_actual_process_added_click");
          } else if (result === "removed") {
            segmentAnalytics("planner_actual_process_removed_click");
          }
          this.toggleOutages(this.getSchedulerPro(), e.eventRecord);
        }
        if (e.eventRecord.getData("type")?.startsWith("process_")) {
          segmentAnalytics("planner_process_click");
          this.processSideBarProcesses = e.eventRecord.getData("processes");
          this.processSideBarResourceFullName = this.getResourceFullName(e.eventRecord.resource);
          this.isProcessSideBarOpen = true;
        }
      }, 200);
    },
    handleEventDblClick(e: SchedulerProBaseEvent) {
      if (this.currentMode === "visitor") {
        this.handleActualSidebarOpen(e);
      }
    },
    handleActualSidebarOpen(e: SchedulerProBaseEvent) {
      clearTimeout(this.clickTimeoutId);
      this.eventDblClickSourceId = e.source.project.resourceStore
        .getById(e.eventRecord.getData("resourceId"))
        .getData("sourceId");
      this.actualSideBarResourceFullName = this.getResourceFullName(e.eventRecord.resource);
      this.eventsOfResource = (
        this.getSchedulerPro().eventStore.getEventsForResource(
          e.eventRecord.getData("resourceId"),
        ) as EventModel[]
      ).filter((event) => !event.getData("type")?.startsWith("process_"));
      if (this.hasPermission(["planner_user"])) {
        const { customer_name, site_id } = this.$route.params;
        PlannerRepository.loadResourceCameraIds(
          customer_name as string,
          site_id as string,
          this.eventDblClickSourceId as string,
        ).then((response) => {
          this.sourceCamerasList = response;
          this.isActualSideBarOpen = true;
        });
      } else {
        this.sourceCamerasList = [];
        this.isActualSideBarOpen = true;
      }
    },
    showProcessesOnEventClick(e: SchedulerProBaseEvent) {
      this.showProcessesForEachChild(e.eventRecord.resource, true);
      const expanded = e.eventRecord.resource.isExpanded(e.eventRecord.resourceStore);
      if (!expanded) {
        this.openResource(e.eventRecord.resource, expanded);
      }
    },
    hideProcessesOnEventClick(e: SchedulerProBaseEvent) {
      this.showProcessesForEachChild(e.eventRecord.resource, false);
    },
    showProcessesOnMenu(e: SchedulerProResourceItem) {
      this.showProcessesForEachChild(e.record, true);
      const expanded = e.record.isExpanded(this.getSchedulerPro().resourceStore);

      if (!expanded) {
        this.openResource(e.record, expanded);
      }
    },
    hideProcessesOnMenu(e: SchedulerProResourceItem) {
      this.showProcessesForEachChild(e.record, false);
    },
    importPlanner(file: File) {
      const { customer_name, site_id } = this.$route.params;
      if (!file) {
        return;
      }

      PlannerRepository.parseXML(customer_name as string, site_id as string, file, "1")
        .then((planConfig) => {
          this.planConfig = planConfig;
          if (this.currentMode !== "revision") this.changeMode("revision");
          setTimeout(() => {
            this.hasChanges = true;
          }, 1000);
        })
        .catch((error) => {
          logger.error(error);
          if (error.response.status === 400) {
            this.showToast("error", this.$t("analytics.planner.wrong_file_format"));
          }
        })
        .finally(() => {
          if (this.openImportModal) this.openImportModal = false;
        });
    },
    clickPlannerImport() {
      if (this.canImport) {
        this.openImportModal = true;
      } else {
        this.showToast("default", this.$t("analytics.planner.no_planner_import_possible"));
      }
    },

    async setPlannerImportStatus() {
      const { customer_name, site_id } = this.$route.params;

      try {
        const response = await apiClient.get<void, AxiosResponse<HierarchyTagStore[]>>(
          `/planner-v2/hierarchy/${customer_name}/${site_id}`,
        );
        const data = response?.data;
        this.canImport = data.length === 0;
      } catch (error) {
        return false;
      }
    },
  },

  setup() {
    const { projectDurationSettings, isProjectDurationSettingsLoading } =
      useProjectDurationSettings(apiClient);
    const { streams } = useStreams();
    const isCompleted = useCurrentProject()?.status === "completed";
    const toggleOutages = useOutages(apiClient, streams, isCompleted);
    const refreshHolidaysInScheduler = useHolidaysInScheduler(apiClient);
    const refreshNonWorkingDaysInScheduler = useNonWorkingDaysInScheduler(apiClient);
    const showSaveBeforeLeaveConfirmationModal = useSaveBeforeLeaveConfirmationModal();
    const showConfirmationModal = useConfirmationModal();

    return {
      streams,
      projectDurationSettings,
      isProjectDurationSettingsLoading,
      refreshHolidaysInScheduler,
      toggleOutages,
      refreshNonWorkingDaysInScheduler,
      showSaveBeforeLeaveConfirmationModal,
      showConfirmationModal,
    };
  },
});
